import React, { useEffect, useState } from "react";
import {
  createRule,
  getReportById,
  getReportField,
  getReportRule,
} from "../../api/reportGeneratorApi";
import { getBulkDropdownData } from "../../api/keyvalue";
import {
  getOtherRefSearchDropdown,
  getTemplateDropdown,
} from "../../api/keyvalue";
import { vehicleAssignmentSearch } from "../../api/jobApi";
import Input from "../../components/input/Input";
import Dropdown from "../../components/input/Dropdown";
import SearchDropdown from "../../components/input/SearchDropdown";
import Button from "../../components/input/Button";
import { toast } from "react-toastify";
import { getBehalfOf } from "../../api/systemUserApi";

const componentTypes = {
  TEXT: 1,
  NUMBER: 2,
  DATE: 3,
  DATETIME: 4,
  DROPDOWN: 5,
  SEARCH_DROPDOWN: 6,
};

const endpointToApi = {
  "/transportservice/keyvalues/dropdownsearch": getOtherRefSearchDropdown,
  "/transportservice/job/vehicleassignment/search": vehicleAssignmentSearch,
  "/transportservice/keyvalues/template-dropdown": getTemplateDropdown,
};

const parseRuleValue = (ruleValue, isMultiple) => {
  if (ruleValue === null || ruleValue === undefined || ruleValue === "")
    return null;
  if (isMultiple) {
    return ruleValue
      .split(",")
      .map((v) => (isNaN(v.trim()) ? v.trim() : Number(v.trim())));
  }
  return ruleValue;
};

const resolveComponentType = (dataType, fieldCopType) => {
  if (!dataType) return null;
  const types = String(dataType)
    .split(",")
    .map((v) => Number(v.trim()))
    .filter(Boolean);
  if (types.length === 0) return null;
  if (types.length === 1) return types[0];
  return types.includes(fieldCopType) ? fieldCopType : types[0];
};

const RuleAssignment = ({
  ruleAssignment = {},
  editMode = false,
  onSuccess,
}) => {
  const [reportTypeId, setReportTypeId] = useState([]);
  const [report, setReport] = useState("");
  const [reportFields, setReportFields] = useState([]);
  const [reportRules, setReportRules] = useState([]);
  const [selectedField, setSelectedField] = useState("");
  const [selectedRule, setSelectedRule] = useState("");
  const [activeOptions, setActiveOptions] = useState([]);
  const [active, setActive] = useState(1);
  const [priority, setPriority] = useState("0");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState("");
  const [userDefaultValue, setUserDefaultValue] = useState("");
  const [errors, setErrors] = useState({});

  const [value, setValue] = useState(null);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([]);

  const [multiValues, setMultiValues] = useState({});
  const [singleSearchEditMode, setSingleSearchEditMode] = useState(false);
  const [singleSearchFieldInfo, setSingleSearchFieldInfo] = useState({
    tableName: "",
    primaryKey: "",
    fieldName: "",
    id: "",
  });

  useEffect(() => {
    (async () => {
      const [reports, bulk] = await Promise.all([
        getReportById(),
        getBulkDropdownData([{ key: "active" }]),
      ]);
      setReportTypeId(reports);
      setActiveOptions(bulk?.active ?? []);
    })();
  }, []);

  useEffect(() => {
    if (!editMode || reportTypeId.length === 0) return;

    const data = ruleAssignment?.reportRuleAssignment;
    if (!data?.id) return;

    (async () => {
      const {
        reportId,
        ruleFieldId,
        ruleDefId,
        ruleValue,
        priority,
        isActive,
        userId,
        userNumericId,
      } = data;

      setReport(reportId);

      const fields = await getReportField(reportId);
      setReportFields(fields);
      setSelectedField(ruleFieldId);

      const fieldObj = fields.find((f) => f.value === ruleFieldId);

      if (fieldObj) {
        const rules = await getReportRule(reportId, fieldObj.field);
        setReportRules(rules);
        setSelectedRule(ruleDefId);

        const ruleObj = rules.find((r) => r.value === ruleDefId);
        const resolvedType = resolveComponentType(
          ruleObj?.dataType,
          fieldObj.copType,
        );

        await loadValueFieldOptions(fieldObj, resolvedType);

        const isMultipleResolved =
          resolvedType === componentTypes.SEARCH_DROPDOWN ||
          resolvedType === componentTypes.DROPDOWN
            ? !!fieldObj.isMultiple
            : false;

        const parsedValue = parseRuleValue(ruleValue, isMultipleResolved);
        setValue(parsedValue);

        if (resolvedType === componentTypes.SEARCH_DROPDOWN && ruleValue) {
          let parsedFieldInfo = {};
          try {
            if (fieldObj.copFieldInfo) {
              parsedFieldInfo = JSON.parse(fieldObj.copFieldInfo);
            }
          } catch {
            parsedFieldInfo = {};
          }

          if (fieldObj.isMultiple) {
            const ids = ruleValue.split(",").map((v) => v.trim());
            const api = endpointToApi[fieldObj.copEndpoint];
            let parsedParams = {};
            try {
              if (fieldObj.copParams)
                parsedParams = JSON.parse(fieldObj.copParams);
            } catch {
              parsedParams = {};
            }
            if (api) {
              try {
                const fetchedArr = await api({
                  ...parsedParams,
                  ids: ids.join(","),
                });
                const arr = Array.isArray(fetchedArr) ? fetchedArr : [];
                const map = {};
                ids.forEach((id) => {
                  const match = arr.find(
                    (item) =>
                      String(item.value) === String(id) ||
                      String(item.id) === String(id),
                  );
                  map[id] = match?.text ?? match?.title ?? match?.label ?? id;
                });
                setMultiValues(map);
              } catch {
                const map = {};
                ids.forEach((id) => {
                  map[id] = id;
                });
                setMultiValues(map);
              }
            }
            setValue(ids.map((id) => (isNaN(id) ? id : Number(id))));
          } else if (parsedFieldInfo.tableName) {
            setSingleSearchFieldInfo({
              ...parsedFieldInfo,
              id: ruleValue,
            });
            setSingleSearchEditMode(true);
            setValue(ruleValue);
          }
        }
      }

      setPriority(String(priority));
      setActive(isActive ? 1 : 2);
      if (userId) {
        setUser(userId);
        if (userNumericId) {
          try {
            const results = await getBehalfOf({
              searchText: String(userNumericId),
              customer: null,
              shipper: null,
            });
            const arr = Array.isArray(results) ? results : [];
            const match = arr.find(
              (item) =>
                String(item.usrId) === String(userNumericId) ||
                String(item.value) === String(userNumericId),
            );
            setUserDefaultValue(
              match?.usrUsername ?? match?.title ?? match?.text ?? userId,
            );
          } catch {
            setUserDefaultValue(userId);
          }
        } else {
          setUserDefaultValue(userId);
        }
      }
    })();
  }, [editMode, ruleAssignment, reportTypeId]);

  const loadValueFieldOptions = async (fieldObj, resolvedType) => {
    if (resolvedType !== componentTypes.DROPDOWN) return;
    if (!fieldObj.copParams) return;

    try {
      const parsedParams = JSON.parse(fieldObj.copParams);
      if (fieldObj.copLiveFetch) {
        const api = endpointToApi[fieldObj.copEndpoint];
        if (api) {
          const result = await api(parsedParams);
          setDropdownOptions(result ?? []);
        }
      } else if (fieldObj.copEndpoint === "/transportservice/keyvalues") {
        const bulkKey = parsedParams.key;
        if (bulkKey) {
          const result = await getBulkDropdownData([{ key: bulkKey }]);
          setDropdownOptions(result[bulkKey] ?? []);
        }
      }
    } catch {
      setDropdownOptions([]);
    }
  };

  const handleReportChange = async (val) => {
    setReport(val);
    setSelectedField("");
    setSelectedRule("");
    setReportFields([]);
    setReportRules([]);
    setValue(null);
    setDropdownOptions([]);
    setMultiValues({});
    setSingleSearchEditMode(false);
    setSingleSearchFieldInfo({
      tableName: "",
      primaryKey: "",
      fieldName: "",
      id: "",
    });
    setErrors({});

    const selected = reportTypeId.find((r) => r.value === val);
    if (selected) {
      const fields = await getReportField(selected.value);
      setReportFields(fields);
    }
  };

  const handleFieldChange = async (val) => {
    setSelectedField(val);
    setSelectedRule("");
    setReportRules([]);
    setValue(null);
    setSearchFieldReset(true);
    setDropdownOptions([]);
    setMultiValues({});
    setSingleSearchEditMode(false);
    setSingleSearchFieldInfo({
      tableName: "",
      primaryKey: "",
      fieldName: "",
      id: "",
    });
    setErrors((prev) => ({
      ...prev,
      selectedField: null,
      selectedRule: null,
      value: null,
    }));

    const selected = reportFields.find((r) => r.value === val);
    if (report && selected) {
      const rules = await getReportRule(report, selected.field);
      setReportRules(rules);
    }
  };

  const handleRuleChange = async (val) => {
    setSelectedRule(val);
    setValue(null);
    setSearchFieldReset(true);
    setDropdownOptions([]);
    setMultiValues({});
    setSingleSearchEditMode(false);
    setSingleSearchFieldInfo({
      tableName: "",
      primaryKey: "",
      fieldName: "",
      id: "",
    });
    setErrors((prev) => ({ ...prev, selectedRule: null, value: null }));

    const fieldObj = reportFields.find((f) => f.value === selectedField);
    const ruleObj = reportRules.find((r) => r.value === val);
    if (!fieldObj || !ruleObj) return;

    const resolvedType = resolveComponentType(
      ruleObj.dataType,
      fieldObj.copType,
    );
    await loadValueFieldOptions(fieldObj, resolvedType);
  };

  const handleRuleReset = () => {
    setReport("");
    setSelectedField("");
    setSelectedRule("");
    setReportFields([]);
    setReportRules([]);
    setActive(1);
    setPriority("0");
    setValue(null);
    setSearchFieldReset(true);
    setDropdownOptions([]);
    setMultiValues({});
    setSingleSearchEditMode(false);
    setSingleSearchFieldInfo({
      tableName: "",
      primaryKey: "",
      fieldName: "",
      id: "",
    });
    setErrors({});
    setUser("");
    setUserDefaultValue("");
  };

  const buildRuleValue = () => {
    if (value === null || value === undefined || value === "") return "";
    if (Array.isArray(value)) return value.join(",");
    return String(value);
  };

  const validate = () => {
    const newErrors = {};

    if (!report) newErrors.report = "Report Type is required.";
    if (!selectedField) newErrors.selectedField = "Field is required.";
    if (!selectedRule) newErrors.selectedRule = "Report Rule is required.";

    if (resolvedComponentType) {
      const ruleValue = buildRuleValue();
      if (!ruleValue || ruleValue.trim() === "") {
        newErrors.value = "Value is required.";
      }
    }

    if (isNaN(Number(priority))) {
      newErrors.priority = "Priority must be a number.";
    }

    if (!active) newErrors.active = "Active is required.";
    if (!user) newErrors.user = "User is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please fill all required fields.", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }

    const payload = {
      reportId: report,
      ruleFieldId: selectedField,
      ruleDefId: selectedRule,
      ruleValue: buildRuleValue(),
      priority: Number(priority),
      isActive: active === 1 || active === "1",
      user: user,
    };

    try {
      setIsSubmitting(true);
      const response = await createRule(payload);
      toast.success(
        response?.message ??
          (editMode
            ? "Rule updated successfully!"
            : "Rule created successfully!"),
        { autoClose: 5000, containerId: "Main" },
      );
      handleRuleReset();
      if (onSuccess) onSuccess();
    } catch {
      toast.error(
        editMode
          ? "Failed to update rule. Please try again."
          : "Failed to create rule. Please try again.",
        { autoClose: 5000, containerId: "Main" },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentField = reportFields.find((f) => f.value === selectedField);
  const currentRule = reportRules.find((r) => r.value === selectedRule);
  const resolvedComponentType =
    currentField && currentRule
      ? resolveComponentType(currentRule.dataType, currentField.copType)
      : null;

  const renderValueField = () => {
    if (!resolvedComponentType) return null;

    const { copParams, copEndpoint, isMultiple, copFieldInfo } = currentField;

    let parsedParams = {};
    try {
      if (copParams) parsedParams = JSON.parse(copParams);
    } catch {
      parsedParams = {};
    }

    let parsedFieldInfo = {};
    try {
      if (copFieldInfo) parsedFieldInfo = JSON.parse(copFieldInfo);
    } catch {
      parsedFieldInfo = {};
    }

    switch (resolvedComponentType) {
      case componentTypes.TEXT:
        return (
          <Input
            required={true}
            label="Value"
            value={value ?? ""}
            error={errors.value}
            resetError={() => setErrors((prev) => ({ ...prev, value: null }))}
            onChange={(e) => {
              setValue(e.target.value);
              setErrors((prev) => ({ ...prev, value: null }));
            }}
          />
        );

      case componentTypes.NUMBER:
        return (
          <Input
            label="Value"
            required={true}
            type="number"
            value={value ?? ""}
            error={errors.value}
            resetError={() => setErrors((prev) => ({ ...prev, value: null }))}
            onChange={(e) => {
              setValue(e.target.value);
              setErrors((prev) => ({ ...prev, value: null }));
            }}
          />
        );

      case componentTypes.DATE:
        return (
          <Input
            label="Value"
            required={true}
            type="date"
            value={value ?? ""}
            error={errors.value}
            resetError={() => setErrors((prev) => ({ ...prev, value: null }))}
            onChange={(e) => {
              setValue(e.target.value);
              setErrors((prev) => ({ ...prev, value: null }));
            }}
          />
        );

      case componentTypes.DATETIME:
        return (
          <Input
            label="Value"
            required={true}
            type="datetime-local"
            value={value ?? ""}
            error={errors.value}
            resetError={() => setErrors((prev) => ({ ...prev, value: null }))}
            onChange={(e) => {
              setValue(e.target.value);
              setErrors((prev) => ({ ...prev, value: null }));
            }}
          />
        );

      case componentTypes.DROPDOWN:
        return (
          <Dropdown
            label="Value"
            required={true}
            multiple={!!isMultiple}
            placeholder="Select Value"
            options={dropdownOptions}
            value={value ?? (isMultiple ? [] : "")}
            error={errors.value}
            resetError={() => setErrors((prev) => ({ ...prev, value: null }))}
            onChange={(e, data) => {
              setValue(data.value);
              setErrors((prev) => ({ ...prev, value: null }));
            }}
          />
        );

      case componentTypes.SEARCH_DROPDOWN: {
        const api = endpointToApi[copEndpoint];

        return isMultiple ? (
          <SearchDropdown
            label="Value"
            required={true}
            occupySpace={false}
            multiple={true}
            returnFullObject={true}
            reset={searchFieldReset}
            setReset={setSearchFieldReset}
            multiValueTwoWayBinding={true}
            multiValues={multiValues}
            error={errors.value}
            resetError={() => setErrors((prev) => ({ ...prev, value: null }))}
            multiValueRemoveHandler={(key) => {
              setMultiValues((prev) => {
                const { [key]: _, ...rest } = prev;
                return rest;
              });
              setValue((prev) =>
                Array.isArray(prev)
                  ? prev.filter((item) => String(item) !== String(key))
                  : [],
              );
              setErrors((prev) => ({ ...prev, value: null }));
            }}
            onResultSelect={(data) => {
              const val = data?.value ?? data?.id ?? data;
              const text =
                data?.text ?? data?.title ?? data?.label ?? String(val);
              setMultiValues((prev) => ({ ...prev, [val]: text }));
              setValue((prev) => {
                const prevArr = Array.isArray(prev) ? prev : [];
                if (!prevArr.map(String).includes(String(val)))
                  return [...prevArr, val];
                return prevArr;
              });
              setErrors((prev) => ({ ...prev, value: null }));
            }}
            api={api}
            apiDependency={parsedParams}
          />
        ) : (
          <SearchDropdown
            label="Value"
            required={true}
            reset={searchFieldReset}
            setReset={setSearchFieldReset}
            clearable={true}
            editMode={singleSearchEditMode}
            fieldInfo={
              singleSearchEditMode
                ? singleSearchFieldInfo
                : { tableName: "", primaryKey: "", fieldName: "", id: "" }
            }
            error={errors.value}
            resetError={() => setErrors((prev) => ({ ...prev, value: null }))}
            onClear={() => {
              setValue(null);
              setSingleSearchEditMode(false);
              setSingleSearchFieldInfo({
                tableName: "",
                primaryKey: "",
                fieldName: "",
                id: "",
              });
              setErrors((prev) => ({ ...prev, value: null }));
            }}
            onResultSelect={(data) => {
              setValue(data);
              setSingleSearchEditMode(false);
              setErrors((prev) => ({ ...prev, value: null }));
            }}
            api={api}
            apiDependency={parsedParams}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <div>
      <form className="ui form" style={{ overflowY: "auto", height: "75vh" }}>
        <div className="ui segment">
          <h4 className="ui dividing header">Rules</h4>
          <div className="two fields">
            <Dropdown
              label="Report Type"
              required={true}
              placeholder="Select Report Type"
              options={reportTypeId}
              value={report}
              error={errors.report}
              resetError={() =>
                setErrors((prev) => ({ ...prev, report: null }))
              }
              onChange={(e, { value }) => {
                handleReportChange(value);
              }}
            />
            <Dropdown
              label="Field"
              required={true}
              placeholder="Select Report Field"
              options={reportFields}
              value={selectedField}
              disabled={!report}
              error={errors.selectedField}
              resetError={() =>
                setErrors((prev) => ({ ...prev, selectedField: null }))
              }
              onChange={(e, { value }) => {
                handleFieldChange(value);
              }}
            />
          </div>
          <div className="two fields">
            <Dropdown
              label="Report Rule"
              required={true}
              options={reportRules}
              placeholder="Select Report Rule"
              value={selectedRule}
              disabled={!selectedField}
              error={errors.selectedRule}
              resetError={() =>
                setErrors((prev) => ({ ...prev, selectedRule: null }))
              }
              onChange={(e, { value }) => handleRuleChange(value)}
            />
            {renderValueField()}
          </div>
          <div className="two fields">
            <SearchDropdown
              key={
                userDefaultValue
                  ? `user-${userDefaultValue}`
                  : "user-dropdown-empty"
              }
              label="User"
              required={true}
              reset={searchFieldReset}
              setReset={setSearchFieldReset}
              clearable={true}
              defaultValue={userDefaultValue}
              api={getBehalfOf}
              apiDependency={{
                customer: null,
                shipper: null,
              }}
              error={errors.user}
              resetError={() => setErrors((prev) => ({ ...prev, user: null }))}
              onClear={() => {
                setUser("");
                setUserDefaultValue("");
                setErrors((prev) => ({ ...prev, user: null }));
              }}
              returnFullObject={true}
              onResultSelect={(data) => {
                const username = data?.usrUsername ?? data?.value ?? data;
                const display = data?.usrUsername ?? String(username);
                setUser(username);
                setUserDefaultValue(display);
                setErrors((prev) => ({ ...prev, user: null }));
              }}
            />
            <Input
              type="number"
              label="Priority"
              value={priority}
              error={errors.priority}
              resetError={() =>
                setErrors((prev) => ({ ...prev, priority: null }))
              }
              onChange={(e) => {
                setPriority(e.target.value);
                setErrors((prev) => ({ ...prev, priority: null }));
              }}
            />
          </div>
          <div className="two fields">
            <Dropdown
              label="Active"
              required={true}
              options={activeOptions}
              value={active}
              error={errors.active}
              resetError={() =>
                setErrors((prev) => ({ ...prev, active: null }))
              }
              onChange={(e, { value }) => {
                setActive(value);
                setErrors((prev) => ({ ...prev, active: null }));
              }}
            />
          </div>
        </div>
        <div>
          <Button
            label={editMode ? "Update" : "Submit"}
            loading={isSubmitting}
            onClick={handleSubmit}
          />
          <Button template="reset" onClick={handleRuleReset} />
        </div>
      </form>
    </div>
  );
};

export default RuleAssignment;
